import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { getFloatField, getIntegerField, getTextField } from '@lender/utils';
import { Button } from '@mui/material';
import { ButtonGroupWrapper } from 'app/shared/componentsRestyled/coreDesignSystem/Buttons';
import { RadioGroupCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FieldInputType } from 'constants/fields';
import { HORIZONTAL_ALIGNMENT, ItemLabelPlacement } from 'constants/ui';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';

import {
  getMonthlyPaymentEstimationOptions,
  MAX_INTEREST_RATE,
  MAX_LOAN_TERM_MONTHS,
  MIN_INTEREST_RATE,
  MonthlyPaymentEstimationType,
} from './CalculateMonthlyPaymentModal.constants';
import { CalculateMonthlyPaymentModalTestIds } from './CalculateMonthlyPaymentModal.testIds';

const getSchema = ({ isCalculatedOption }) =>
  object().shape({
    monthlyPaymentEstimation: getTextField().nullable(),
    months: getIntegerField({ isRequired: isCalculatedOption })
      .nullable()
      .min(1)
      .max(MAX_LOAN_TERM_MONTHS),
    percent: getFloatField({ isRequired: isCalculatedOption })
      .nullable()
      .min(MIN_INTEREST_RATE)
      .max(MAX_INTEREST_RATE),
  });

const CalculateMonthlyPaymentModal = ({ unpaidBalance, formRef, onSubmit: onSubmitProps }) => {
  const dispatch = useDispatch();

  const { form, isFormChanged, validationErrors, setSchema, validateForm, handleChange } = useForm({
    monthlyPaymentEstimation: undefined,
    months: 12,
    percent: 5,
  });

  const isCalculatedOption = form.monthlyPaymentEstimation === MonthlyPaymentEstimationType.UB_CALC;

  const monthlyPaymentEstimationOptions = useMemo(
    () => getMonthlyPaymentEstimationOptions(form, { unpaidBalance, isCalculatedOption }),
    [unpaidBalance, form, isCalculatedOption],
  );

  useEffect(() => {
    setSchema(getSchema({ isCalculatedOption }));
  }, [setSchema, isCalculatedOption]);

  const onCancel = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const { monthlyPayment } = monthlyPaymentEstimationOptions.find(
        i => i.value === form.monthlyPaymentEstimation,
      );

      onSubmitProps(monthlyPayment);
      onCancel();
    },
    [
      form.monthlyPaymentEstimation,
      validateForm,
      onCancel,
      monthlyPaymentEstimationOptions,
      onSubmitProps,
    ],
  );

  return (
    <form
      noValidate
      className="w-full"
      data-ischanged={isFormChanged}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <RadioGroupCDS
        required
        dataTestId={CalculateMonthlyPaymentModalTestIds.ESTIMATION_RADIO}
        error={!!validationErrors.monthlyPaymentEstimation}
        labelPlacement={ItemLabelPlacement.END}
        name="monthlyPaymentEstimation"
        options={monthlyPaymentEstimationOptions}
        value={form.monthlyPaymentEstimation}
        onChange={handleChange}
      />

      {isCalculatedOption && (
        <div className="flex items-center mt-5">
          <div className="w-128 mr-4">
            <TextFieldCDS
              required
              dataTestId={CalculateMonthlyPaymentModalTestIds.PERCENT_INPUT}
              decimalScale={3}
              decimalSeparator="."
              error={!!validationErrors.percent}
              id="percent"
              name="percent"
              type={FieldInputType.PERCENTAGE_RESTYLED}
              value={form.percent}
              variant="outlined"
              onChange={handleChange}
            />
          </div>
          {` of Unpaid Balance divided by `}
          <div className="w-64 mx-4">
            <TextFieldCDS
              required
              dataTestId={CalculateMonthlyPaymentModalTestIds.MONTHS_INPUT}
              error={!!validationErrors.months}
              id="months"
              maxValue={9999}
              name="months"
              type={FieldInputType.INT}
              value={form.months}
              variant="outlined"
              onChange={handleChange}
            />
          </div>
          {` months`}
        </div>
      )}

      <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} className="w-full pt-24 px-4">
        <Button
          className="dialog-action"
          data-testid={CalculateMonthlyPaymentModalTestIds.CANCEL_BUTTON}
          size="large"
          variant="line"
          onClick={onCancel}
        >
          Cancel
        </Button>

        <Button
          data-testid={CalculateMonthlyPaymentModalTestIds.SAVE_BUTTON}
          disabled={!isFormChanged}
          size="large"
          type="submit"
        >
          Save
        </Button>
      </ButtonGroupWrapper>
    </form>
  );
};

CalculateMonthlyPaymentModal.propTypes = {
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  unpaidBalance: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default CalculateMonthlyPaymentModal;
